/* eslint-disable max-len */
import { captureException } from '@sentry/react'
import React, { useEffect, useState } from 'react'
import { Actions } from '~/actions'
import type { LineItem } from '~/types'
import { filesUrl, includes, lineComparePrice, linePrice, productUrl, useDispatch, useSelector } from '~/utils'
import { Price } from '../utils/Price'
import './CartItem.less'

interface Props {
	lineItem: LineItem
}

export const CartItem: React.FC<Props> = ({ lineItem }) => {
	const pendingOperation = useSelector(s => s.cart.pendingOperation)
	const [cachedPrices, setCachedPrices] = useState([0, 0])
	const dispatch = useDispatch()
	const { id, quantity, merchandise: variant } = lineItem

	// Cache prices to better optimistically update prices with automatic prices
	useEffect(() => {
		if (!pendingOperation) {
			setCachedPrices([lineComparePrice(lineItem) / quantity, linePrice(lineItem) / quantity])
		}
	}, [pendingOperation, lineItem, quantity])

	if (!variant) {
		captureException(new Error(`Variant for product ${lineItem.merchandise.title} missing`))
		return null
	}

	const _productUrl = productUrl(variant.product)
	return (
		<li className="cart__item">
			<div className="cart__item__image" style={{ backgroundImage: `url(${variant.image?.url}&height=100)` }} />
			<a className="cart__item__title" href={location.pathname.includes(_productUrl) ? undefined : _productUrl}>
				{variant.product.title || variant.title}
			</a>
			<button className="button cart__item__remove" onClick={_ => dispatch(Actions.removeFromCart(id))}>
				<img src={filesUrl('trash.svg')} alt="delete" />
			</button>

			<span className="cart__item__desc">{getDescription(variant)}</span>

			<div className="cart__item__amount">
				<div className="cart__item__adjust">
					<button
						className="button cart__item__adjust__btn"
						onClick={_ => dispatch(Actions.updateQuantity(quantity - 1, id))}
						disabled={quantity == 1}
					>
						<span>-</span>
					</button>
					<span className="cart__item__quantity">{quantity}</span>
					<button
						className="button cart__item__adjust__btn"
						onClick={_ => dispatch(Actions.updateQuantity(quantity + 1, id))}
					>
						<span>+</span>
					</button>
				</div>
			</div>

			<span className="cart__item__price">
				{pendingOperation && includes(pendingOperation, lineItem.id, variant.id) ? (
					<Price original={cachedPrices[0] * quantity} price={cachedPrices[1] * quantity} asText />
				) : (
					<Price
						original={lineComparePrice(lineItem)}
						price={linePrice(lineItem)}
						currencyCode={variant.price.currencyCode}
						asText
					/>
				)}
			</span>
		</li>
	)
}

function getDescription(variant: NonNullable<LineItem['merchandise']>) {
	return (
		variant.selectedOptions
			.filter(opt => opt.value != 'Default Title')
			.map(opt => opt.value)
			.join(' / ') || variant.product.subtitle?.value
	)
}
